import React, { useCallback, useEffect, useRef, useState } from 'react'

import { ModalProvider } from '@gousto-internal/citrus-react'

import type { SubscriptionSettingsAnalytics } from '@library/api-customer-engagement-experience'
import { queueTrackingEvent } from '@library/tracking-integration'

import type { StagedResubscriptionModal } from '../helpers/resubscriptionModalHandoff'
import { ResubscriptionConfirmationModal } from './ResubscriptionConfirmationModal'

type ResubscriptionConfirmationModalHostProps = {
  staged: StagedResubscriptionModal | null
  focusTargetId?: string
  onClosed?: () => void
}

export const ResubscriptionConfirmationModalHost = ({
  staged,
  focusTargetId,
  onClosed,
}: ResubscriptionConfirmationModalHostProps) => {
  const [isOpen, setIsOpen] = useState(true)
  const hasQueuedPageLoad = useRef(false)

  useEffect(() => {
    if (staged?.pageLoad && !hasQueuedPageLoad.current) {
      hasQueuedPageLoad.current = true
      queueTrackingEvent<SubscriptionSettingsAnalytics>(staged.pageLoad)
    }
  }, [staged])

  const handleClosed = useCallback(() => {
    setIsOpen(false)
    onClosed?.()
  }, [onClosed])

  // The element that opened the modal belongs to the previous route, so focus goes to the menu instead.
  const handleDismissed = useCallback(() => {
    if (!focusTargetId) {
      return
    }
    setTimeout(() => document.getElementById(focusTargetId)?.focus(), 0)
  }, [focusTargetId])

  if (!staged) {
    return null
  }

  // The modal registry is scoped to the feature's own Citrus instance, so it carries its own provider.
  return (
    <ModalProvider>
      <ResubscriptionConfirmationModal
        data={staged.data}
        isOpen={isOpen}
        onClosed={handleClosed}
        onDismissed={handleDismissed}
      />
    </ModalProvider>
  )
}
